/**
 * Shared "can this template actually be sent?" check for the UI.
 *
 * A template can be APPROVED on Meta yet unsendable in our system because its
 * media header (or a carousel card) has no media we can deliver. This is the
 * frontend twin of the backend's send gate, and it combines TWO signals so the
 * UI reflects the true state immediately, everywhere (chat picker, workflow
 * picker, templates list filter/badge):
 *
 *   1. `mediaHealth === 'needs_reupload'` — the authoritative backend flag, set
 *      when a send failed because the media expired or its source went missing.
 *      This is the ONLY way to know about an EXPIRED id (the components still
 *      look complete), so it must be honored.
 *   2. Structural check — a media header/card with no cached id AND no stored S3
 *      copy AND no link. This catches "approved but media never uploaded"
 *      (e.g. templates synced from Meta) WITHOUT waiting for a send attempt to
 *      lazily set the flag, so the 8-ish pre-existing ones surface right away.
 *
 * Deliberately conservative: a TEXT header, a header-less template, or any media
 * header with at least one usable source is considered sendable.
 */

/** The media-header fields this helper reads — a structural subset so BOTH the
 *  full `WhatsAppTemplate` and the trimmed "lite" template types used by the
 *  campaign/workflow pickers satisfy it without casts. */
interface MediaHeaderLike {
  type?: string;
  headerMediaId?: string;
  headerMediaS3Key?: string;
  headerMediaUrl?: string;
}

/** A media header (IMAGE/VIDEO/DOCUMENT) with no deliverable media from any source. */
function headerHasNoMedia(header: MediaHeaderLike | undefined | null): boolean {
  if (!header || !header.type || header.type === 'TEXT') return false;
  return !header.headerMediaId && !header.headerMediaS3Key && !header.headerMediaUrl;
}

/** Minimal structural shape this helper needs — a full template or any lean row. */
export interface TemplateMediaCheckable {
  mediaHealth?: string | null;
  components?: {
    header?: MediaHeaderLike | null;
    carousel?: { cards?: Array<{ header?: MediaHeaderLike | null } | null | undefined> } | null;
  } | null;
}

/** True when the template cannot currently be sent because of its media. */
export function isTemplateMediaUnfit(t: TemplateMediaCheckable | null | undefined): boolean {
  if (!t) return false;
  if (t.mediaHealth === 'needs_reupload') return true;

  const cards = t.components?.carousel?.cards;
  if (cards && cards.length > 0) {
    return cards.some((c) => headerHasNoMedia(c?.header));
  }
  return headerHasNoMedia(t.components?.header);
}

/** The media kind a template delivers (top-level header, else its first carousel
 *  card) — drives the image/video/document icon in the pickers. */
export function templateMediaKind(t: TemplateMediaCheckable | null | undefined):
  | 'image'
  | 'video'
  | 'document'
  | null {
  const type = t?.components?.header?.type || t?.components?.carousel?.cards?.[0]?.header?.type;
  switch (type) {
    case 'IMAGE':
      return 'image';
    case 'VIDEO':
      return 'video';
    case 'DOCUMENT':
      return 'document';
    default:
      return null;
  }
}

/** "re_engagement_1" → "Re Engagement 1" — the human label shown in pickers. */
export function formatTemplateName(name: string): string {
  return name
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Short, consistent label/hint shown wherever an unfit template appears. */
export const UNFIT_TEMPLATE_LABEL = 'Media missing';
export const UNFIT_TEMPLATE_HINT =
  'This template can’t be sent — its media needs uploading. Open it to upload the image/video.';
